import { Check, RotateCcw } from "lucide-react";
import { DocumentDiff } from "../../components/DocumentDiff";
import type { ChangeItem } from "../../lib/types";

/**
 * SUGGESTION CARD — one editorial comment on the document.
 *
 * The card body is `DocumentDiff` verbatim (the lawyer-grade before/after with sealed protected
 * chips), so the diff on the desk and the diff in this rail make the same promise in the same
 * words. Collapsed, it shows only its subject and verdict; focused, it opens to the full diff.
 * The two verbs carry equal weight — the product must never nudge toward accepting a machine edit.
 */
export function SuggestionCard({
  change,
  focused,
  ordinal,
  onFocus,
  onAccept,
  onKeep
}: {
  change: ChangeItem;
  focused: boolean;
  ordinal: string;
  onFocus: () => void;
  onAccept: () => void;
  onKeep: () => void;
}) {
  const verdict = change.verdict;
  return (
    <article
      className="sugg-card"
      data-focused={focused ? "true" : "false"}
      data-verdict={verdict}
      data-testid="suggestion-card"
    >
      <button type="button" className="sugg-summary" onClick={onFocus} aria-expanded={focused}>
        <span className="sugg-ordinal">{ordinal}</span>
        <span className="sugg-heading">
          <span className="sugg-title">{change.title}</span>
          <span className="sugg-page">{change.page}</span>
        </span>
        <span className="sugg-state" data-verdict={verdict}>
          {verdict === "approved" ? "Accepted" : verdict === "rejected" ? "Kept" : "Open"}
        </span>
      </button>

      {focused ? (
        <div className="sugg-detail">
          <DocumentDiff
            before={change.before}
            after={change.after}
            beforeLabel="Current"
            afterLabel="Suggested"
          />
          <p className="sugg-reason">{change.reason}</p>
          <div className="sugg-actions">
            <button
              type="button"
              className="sugg-btn"
              data-active={verdict === "approved" ? "true" : "false"}
              onClick={onAccept}
              data-testid="suggestion-accept"
            >
              <Check aria-hidden="true" />
              Accept
            </button>
            <button
              type="button"
              className="sugg-btn"
              data-active={verdict === "rejected" ? "true" : "false"}
              onClick={onKeep}
              data-testid="suggestion-keep"
            >
              <RotateCcw aria-hidden="true" />
              Keep original
            </button>
          </div>
        </div>
      ) : (
        <p className="sugg-collapsed-reason">{change.reason}</p>
      )}
    </article>
  );
}
